// Account Activity tab — reverse-chronological timeline.
//
// Fetches ``GET /api/v1/accounts/{account_id}/activity`` and renders
// the page as day-grouped activity rows: title, description, actor,
// timestamp, and (where the source is known) a deep link back to the
// originating record. "Load more" appends the next cursor page.

import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import {
  useCommitmentLinkProps,
} from "@/features/commitments/commitmentLink";

import { useAccountActivityInfinite } from "./activityHooks";
import type {
  AccountActivityItem, AccountActivityKind,
} from "./activityApi";


export interface AccountActivityTabProps {
  accountId: string;
}


export function AccountActivityTab({ accountId }: AccountActivityTabProps) {
  const query = useAccountActivityInfinite(accountId);

  const items: AccountActivityItem[] = useMemo(() => {
    const pages = query.data?.pages ?? [];
    return pages.flatMap((p) => p.items);
  }, [query.data]);

  const groups = useMemo(() => groupByDay(items), [items]);

  if (query.isLoading) {
    return (
      <section
        data-testid="account-activity-tab"
        className="flex flex-col gap-4 rounded-md border border-border-subtle bg-canvas/40 p-6"
      >
        <DashboardLoadingState variant="card" />
      </section>
    );
  }

  if (query.isError) {
    return (
      <section
        data-testid="account-activity-tab"
        className="flex flex-col gap-4 rounded-md border border-border-subtle bg-canvas/40 p-6"
      >
        <DashboardErrorState
          title="Couldn't load Activity"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section
        data-testid="account-activity-tab"
        className="flex flex-col gap-4 rounded-md border border-border-subtle bg-canvas/40 p-6"
      >
        <DashboardEmptyState
          title="No account activity yet"
          description={
            "Activity will appear here when your team creates "
            + "commitments, applies playbooks, uploads files, "
            + "completes touchpoints, updates contacts, or changes "
            + "plan years for this Account."
          }
        />
      </section>
    );
  }

  // Mirrors the CommitmentListTable per-group sticky pattern: each
  // day group is its own ``<section>`` (block-level, no flex around
  // the sticky element); inside, the day header is a direct child
  // with ``sticky top-0``. The outer wrapper is ``flex flex-col gap-3``
  // exactly like the Tracker's grouped-infinite body, which is the
  // version known to behave correctly across scrolling ancestors.
  return (
    <div
      data-testid="account-activity-tab"
      className="flex flex-col gap-3"
    >
      <ol
        data-testid="account-activity-list"
        className="contents"
      >
        {groups.map((group) => (
          <li
            key={group.dayKey}
            data-testid={`account-activity-group-${group.dayKey}`}
            className="contents"
          >
            <section
              className="rounded-md border border-border-subtle bg-canvas/40"
            >
              <h3
                data-testid={`account-activity-day-${group.dayKey}`}
                // Pin 24px above the scroll container's padding edge
                // (``-top-6``) so the bar lands at the outside-top
                // of the DashboardLayout's scroll <main>, whose
                // own ``py-6`` would otherwise leave a 24px gap of
                // empty space + scrolling rows above the bar.
                className="sticky -top-6 z-20 rounded-t-md border-b border-border-subtle bg-canvas px-4 py-2 text-sm font-semibold text-text-primary"
              >
                {group.label}
              </h3>
              <ul className="flex flex-col gap-3 px-4 py-4">
                {group.items.map((item) => (
                  <ActivityRow
                    key={item.id}
                    item={item}
                    accountId={accountId}
                  />
                ))}
              </ul>
            </section>
          </li>
        ))}
      </ol>
      {query.hasNextPage && (
        <LoadSentinel
          fetchNextPage={query.fetchNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
        />
      )}
    </div>
  );
}


function LoadSentinel({
  fetchNextPage, isFetchingNextPage,
}: {
  fetchNextPage: () => unknown;
  isFetchingNextPage: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // The fetch fn is intentionally tracked in a ref so the observer
  // effect doesn't re-subscribe on every render (TanStack regenerates
  // ``fetchNextPage`` between renders). The observer's job is to fire
  // on each intersection; the latest fetch reference is read from
  // the ref at firing time.
  const fetchRef = useRef(fetchNextPage);
  fetchRef.current = fetchNextPage;
  const isFetchingRef = useRef(isFetchingNextPage);
  isFetchingRef.current = isFetchingNextPage;

  useEffect(() => {
    const target = ref.current;
    if (!target) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !isFetchingRef.current) {
          fetchRef.current();
        }
      }
    }, { rootMargin: "200px 0px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid="account-activity-load-sentinel"
      aria-hidden
      className="h-8 w-full text-center text-[11px] text-text-secondary"
    >
      {isFetchingNextPage ? "Loading…" : ""}
    </div>
  );
}


function ActivityRow({
  item, accountId,
}: { item: AccountActivityItem; accountId: string }) {
  const commitmentLinkProps = useCommitmentLinkProps();
  const link = buildSourceLink({
    item, accountId, commitmentLinkProps,
  });
  return (
    <li
      data-testid="account-activity-item"
      data-kind={item.kind}
      className="flex gap-3 rounded-md border border-border-subtle bg-canvas/60 p-3"
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-canvas text-xs font-semibold text-text-primary"
      >
        {iconFor(item.kind)}
      </span>
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <p
            data-testid="account-activity-item-title"
            className="text-sm font-medium text-text-primary"
          >
            {item.title}
          </p>
          <time
            data-testid="account-activity-item-time"
            dateTime={item.occurred_at}
            className="text-[11px] text-text-secondary"
          >
            {formatTime(item.occurred_at)}
          </time>
        </div>
        {item.description && (
          <p
            data-testid="account-activity-item-description"
            className="text-sm text-text-secondary"
          >
            {item.description}
          </p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-text-secondary">
          {item.actor_name && (
            <span data-testid="account-activity-item-actor">
              {item.actor_name}
            </span>
          )}
          {link && (
            <Link
              to={link.to}
              state={link.state}
              data-testid="account-activity-item-source-link"
              className="text-teal hover:underline"
            >
              {link.label}
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}


interface DayGroup {
  dayKey: string;
  label: string;
  items: AccountActivityItem[];
}


function groupByDay(items: AccountActivityItem[]): DayGroup[] {
  const today = startOfDay(new Date());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const map = new Map<string, DayGroup>();
  for (const it of items) {
    const d = startOfDay(new Date(it.occurred_at));
    const dayKey = d.toISOString().slice(0, 10);
    let label: string;
    if (sameDay(d, today)) {
      label = "Today";
    } else if (sameDay(d, yesterday)) {
      label = "Yesterday";
    } else {
      label = d.toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    }
    const existing = map.get(dayKey);
    if (existing) {
      existing.items.push(it);
    } else {
      map.set(dayKey, { dayKey, label, items: [it] });
    }
  }
  return Array.from(map.values());
}


function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}


function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );
}


function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}


function iconFor(kind: AccountActivityKind): string {
  switch (kind) {
    case "commitment_created":
    case "commitment_status_changed":
    case "commitment_completed":
    case "commitment_cancelled":
    case "commitment_blocked":
      return "C";
    case "playbook_applied":
      return "P";
    case "file_uploaded":
    case "file_linked":
      return "F";
    case "touchpoint_created":
    case "touchpoint_completed":
      return "T";
    case "plan_year_created":
    case "plan_year_updated":
      return "Y";
    case "account_contact_created":
    case "account_contact_updated":
      return "@";
    case "account_team_member_added":
    case "account_team_member_removed":
    case "account_lead_changed":
      return "U";
    default:
      return "•";
  }
}


function buildSourceLink({
  item, accountId, commitmentLinkProps,
}: {
  item: AccountActivityItem;
  accountId: string;
  commitmentLinkProps: ReturnType<typeof useCommitmentLinkProps>;
}):
  {
    to: string;
    label: string;
    state?: Record<string, unknown>;
  } | null {
  if (!item.source_kind || !item.source_id) return null;
  switch (item.source_kind) {
    case "commitment": {
      // Open the Commitment as a route-overlay modal so the Activity
      // tab stays mounted underneath and the modal close pops back
      // here (matches the Tracker / Kanban / dashboard widgets).
      const props = commitmentLinkProps(item.source_id, {
        fromLabel: "Account Activity",
      });
      return {
        to: props.to,
        state: props.state,
        label: "View commitment",
      };
    }
    case "touchpoint":
      return {
        to:
          `/dashboard/accounts/${encodeURIComponent(accountId)}`
          + `?tab=touchpoints`,
        label: "View touchpoint",
      };
    case "file":
      return {
        to:
          `/dashboard/accounts/${encodeURIComponent(accountId)}`
          + `?tab=documents`,
        label: "View file",
      };
    case "playbook_application":
      return {
        to:
          `/dashboard/commitments?playbook_application_id=`
          + encodeURIComponent(item.source_id),
        label: "View generated commitments",
      };
    case "plan_year":
      return {
        to:
          `/dashboard/accounts/${encodeURIComponent(accountId)}`
          + `?tab=overview`,
        label: "View plan year",
      };
    case "account_contact":
      return {
        to:
          `/dashboard/accounts/${encodeURIComponent(accountId)}`
          + `?tab=contacts`,
        label: "View contact",
      };
    case "account_team_member":
      return {
        to:
          `/dashboard/accounts/${encodeURIComponent(accountId)}`
          + `?tab=overview`,
        label: "View team member",
      };
    default:
      return null;
  }
}
